"use client";

import React from "react";
import { LibraryCard } from "./LibraryCard";
import { EmptyLibraryState } from "./EmptyLibraryState";
import { type LibraryArticle } from "@/types/libraryType";

interface LibraryListProps {
  articles: LibraryArticle[] | undefined;
  loading?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
}

export function LibraryList({
  articles,
  loading = false,
  onLoadMore,
  hasMore = false,
}: LibraryListProps) {
  // No categories on this screen; show all articles as-is.
  if ((!articles || articles.length === 0) && !loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <EmptyLibraryState
          title="Chưa có tài liệu"
          description="Hiện tại chưa có tài liệu nào trong thư viện. Vui lòng quay lại sau."
        />
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      {/* Header */}
      <div className="text-center mb-12">
        <h2 className="text-3xl font-bold text-gray-900 mb-4">
          Thư viện tài liệu
        </h2>
        <p className="text-lg text-gray-600 max-w-3xl mx-auto">
          Khám phá bộ sưu tập tài liệu phong phú về pháp luật môi trường, kiến
          thức chuyên ngành và hướng dẫn sử dụng hệ thống KTX.
        </p>
      </div>

      {/* Articles Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-white rounded-xl shadow-lg overflow-hidden animate-pulse"
            >
              <div className="h-48 bg-gray-200" />
              <div className="p-6">
                <div className="h-4 bg-gray-200 rounded mb-2" />
                <div className="h-6 bg-gray-200 rounded mb-2" />
                <div className="h-4 bg-gray-200 rounded mb-4" />
                <div className="flex justify-between">
                  <div className="h-4 bg-gray-200 rounded w-20" />
                  <div className="h-4 bg-gray-200 rounded w-16" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !articles || articles.length === 0 ? (
        <EmptyLibraryState
          title="Không có tài liệu"
          description="Hiện tại chưa có tài liệu nào. Vui lòng quay lại sau."
        />
      ) : (
        <>
          {/* Results count */}
          <div className="mb-6 text-center">
            <p className="text-gray-600">
              Hiển thị{" "}
              <span className="font-semibold">{articles?.length || 0}</span> tài
              liệu
            </p>
          </div>

          {/* Articles Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {articles?.map((article) => (
              <div key={article.id} className="flex justify-center">
                <LibraryCard article={article} size="medium" />
              </div>
            ))}
          </div>

          {/* Load More Button */}
          {hasMore && onLoadMore && (
            <div className="text-center mt-12">
              <button
                onClick={onLoadMore}
                className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-3 rounded-lg font-medium transition-colors duration-200 shadow-lg hover:shadow-xl transform hover:scale-105"
              >
                Tải thêm tài liệu
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
